/**
 * EVALUATION REPORT - Executive Edition
 * =====================================
 * Diseño ejecutivo con tipografía profesional, espaciado generoso
 * y experiencia minimalista elegante en móvil.
 * 
 * Mejoras UX/UI:
 * - Tipografía Inter/SF Pro con tracking optimizado
 * - Espaciado consistente y generoso (24px/32px gaps)
 * - Bordes redondeados suaves (rounded-2xl)
 * - Sombras sutiles con hover states
 * - Responsive mobile-first con breakpoints refinados
 * - Gradientes sutiles para profundidad visual
 */

import React, { useState, useEffect } from 'react';
import { 
  X, TrendingUp, TrendingDown, Award, AlertTriangle,
  CheckCircle, Target, Clock, MessageSquare, Lightbulb,
  Download, Share2, Loader2, ArrowLeft
} from 'lucide-react';
import { getSafeToken } from '../../../utils/tokenHelper';
import { VERTEX_API_BASE } from '../../../utils/vertexApiHelper';

const EvaluationReport = ({ sessionId, onClose, hideHeader = false, backLabel = 'Volver al simulador' }) => {
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedSessionId, setFetchedSessionId] = useState(null);

  useEffect(() => {
    if (fetchedSessionId === sessionId) return;
    fetchEvaluation();
  }, [sessionId]);

  const fetchEvaluation = async () => {
    try {
      setFetchedSessionId(sessionId);
      const token = getSafeToken();
      // Motor V4: endpoint canónico para evaluación
      const endpoint = `${VERTEX_API_BASE}/v4/session/${sessionId}/evaluation`;
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': token ? `Bearer ${token}` : undefined }
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Error al cargar evaluación');

      // Normalización defensiva: valores por defecto para campos críticos
      const normalized = {
        ...data.evaluation,
        skills: data.evaluation?.skills || [],
        strengths: data.evaluation?.strengths || [],
        improvements: data.evaluation?.improvements || [],
        nextSteps: data.evaluation?.nextSteps || [],
        metrics: data.evaluation?.metrics || {
          totalTurns: 0,
          phasesCompleted: 0,
          totalPhases: 0,
          criticalErrors: 0,
          duration: 'N/A'
        },
        overallScore: data.evaluation?.overallScore ?? 0,
        overallGrade: data.evaluation?.overallGrade || 'N/A'
      };

      setEvaluation(normalized);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const gradeColors = {
    'A': { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-500', gradient: 'from-emerald-500 to-green-600' },
    'B': { bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-500', gradient: 'from-blue-500 to-indigo-600' },
    'C': { bg: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-500', gradient: 'from-yellow-500 to-amber-600' },
    'D': { bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-500', gradient: 'from-orange-500 to-red-600' },
    'F': { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-500', gradient: 'from-red-500 to-rose-600' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-base font-medium text-slate-600">Cargando evaluación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full border border-slate-200">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2 tracking-tight">Error al cargar</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">{error}</p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-sm hover:shadow"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  const grade = evaluation.overallGrade;
  const gradeStyle = gradeColors[grade] || gradeColors['C'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 overflow-x-hidden w-full">
      <div className="w-full max-w-6xl mx-auto px-2 sm:px-6 lg:px-8 py-3 sm:py-10 lg:py-12">
        
        {/* Header ejecutivo - Condicional */}
        {!hideHeader && (
          <header className="mb-4 sm:mb-10 px-1">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white hover:shadow-sm rounded-xl transition-all duration-200 mb-3 sm:mb-8 border border-transparent hover:border-slate-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{backLabel}</span>
              <span className="sm:hidden">Volver</span>
            </button>
            
            <div className="space-y-1 sm:space-y-2">
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                Reporte de Evaluación
              </h1>
              <p className="text-xs sm:text-base text-slate-600 font-medium">
                Sesión <span className="font-mono text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{evaluation.sessionId?.slice(-8) || sessionId.slice(-8)}</span>
              </p>
            </div>
          </header>
        )}

        <div className="space-y-3 sm:space-y-8">

          {/* Score General - Hero Card */}
          <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200/80 p-3 sm:p-8 lg:p-10 hover:shadow-md transition-shadow duration-300">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 lg:gap-10">
              
              {/* Texto */}
              <div className="flex-1 space-y-2 sm:space-y-4 w-full">
                <h2 className="text-lg sm:text-2xl font-bold text-slate-900 tracking-tight">Score General</h2>
                <p className="text-sm sm:text-base leading-relaxed text-slate-600 max-w-2xl break-words">
                  {evaluation.summary}
                </p>
              </div>
              
              {/* Scores */}
              <div className="flex items-center justify-center w-full lg:w-auto gap-6 sm:gap-8 lg:gap-10 self-center lg:self-auto pt-3 lg:pt-0 border-t lg:border-t-0 border-slate-100 mt-1 lg:mt-0">
                <div className="text-center">
                  <div className="text-4xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-br from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1 tracking-tight tabular-nums">
                    {evaluation.overallScore}
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 font-semibold">de 100</p>
                </div>

                <div className={`
                  w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-xl sm:rounded-2xl flex items-center justify-center
                  ${gradeStyle.bg} ring-2 sm:ring-4 ${gradeStyle.ring} ring-offset-2 sm:ring-offset-4 shadow-lg transform hover:scale-105 transition-transform duration-300 flex-shrink-0
                `}>
                  <span className={`text-3xl sm:text-5xl lg:text-6xl font-bold ${gradeStyle.text} tracking-tight`}>
                    {grade}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Competencias - Diseño Simplificado (Lista limpia sin cajas anidadas) */}
          {evaluation.skills && evaluation.skills.length > 0 ? (
          <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200/80 px-3 py-2 sm:p-8 hover:shadow-md transition-shadow duration-300">
            <div className="py-3 border-b border-slate-100 mb-1">
              <h2 className="text-base sm:text-xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3 tracking-tight">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                Evaluación por Competencias
              </h2>
            </div>
            
            <div className="divide-y divide-slate-100">
              {evaluation.skills.map((skill, idx) => {
                const skillGrade = gradeColors[skill.grade] || gradeColors['C'];
                
                return (
                  <article key={idx} className="py-4 sm:py-6 group first:pt-2">
                    
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2 sm:mb-4 gap-2 sm:gap-3">
                      <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                        <span className={`
                          w-8 h-8 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center text-base sm:text-xl font-bold flex-shrink-0
                          ${skillGrade.bg} ${skillGrade.text} shadow-sm
                        `}>
                          {skill.grade}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-sm sm:text-lg text-slate-900 tracking-tight truncate pr-1">{skill.name}</h3>
                          <p className="text-[10px] sm:text-sm text-slate-500 font-medium mt-0.5">Peso: {skill.weight}%</p>
                        </div>
                      </div>
                      <span className="text-xl sm:text-4xl font-bold text-slate-900 ml-1 sm:ml-4 tabular-nums tracking-tight flex-shrink-0">{skill.score}</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-100 rounded-full h-1.5 sm:h-3 mb-3 sm:mb-4 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-700 ease-out bg-gradient-to-r ${
                          skill.score >= 80 ? 'from-emerald-500 to-green-600' :
                          skill.score >= 60 ? 'from-yellow-500 to-amber-600' : 
                          'from-red-500 to-rose-600'
                        }`}
                        style={{ width: `${skill.score}%` }}
                      />
                    </div>

                    {/* Evidencia - Diseño limpio sin caja gris */}
                    {skill.evidence?.length > 0 && (
                      <div className="mt-2 pl-3 sm:pl-4 border-l-2 border-indigo-100">
                        <p className="text-sm sm:text-base leading-relaxed text-slate-700">
                          <strong className="font-semibold text-slate-900 block mb-1">Evidencia:</strong>
                          {skill.evidence[0]}
                        </p>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
          ) : (
            <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200/80 px-3 py-2 sm:p-8">
              <div className="py-3 border-b border-slate-100 mb-1">
                <h2 className="text-base sm:text-xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3 tracking-tight">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />
                  </div>
                  Evaluación por Competencias
                </h2>
              </div>
              <div className="py-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <p className="text-slate-600">No hay datos de competencias disponibles.</p>
              </div>
            </section>
          )}

          {/* Fortalezas y Mejoras */}
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
            
            {/* Fortalezas */}
            {evaluation.strengths?.length > 0 && (
              <section className="bg-gradient-to-br from-emerald-50/80 to-green-50/60 border-2 border-emerald-200/60 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h2 className="text-lg sm:text-xl font-bold text-emerald-900 mb-5 sm:mb-6 flex items-center gap-3 tracking-tight">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                  </div>
                  Fortalezas
                </h2>
                <ul className="space-y-4">
                  {evaluation.strengths.map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-3 group">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <p className="text-sm sm:text-base font-medium text-emerald-950 leading-relaxed">{strength.skill}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Mejoras */}
            {evaluation.improvements?.length > 0 && (
              <section className="bg-gradient-to-br from-amber-50/80 to-orange-50/60 border-2 border-amber-200/60 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h2 className="text-lg sm:text-xl font-bold text-amber-900 mb-5 sm:mb-6 flex items-center gap-3 tracking-tight">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                  </div>
                  Áreas de Mejora
                </h2>
                <ul className="space-y-4">
                  {evaluation.improvements.map((improvement, idx) => (
                    <li key={idx} className="flex items-start gap-3 group">
                      <Target className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <p className="text-sm sm:text-base font-medium text-amber-950 leading-relaxed">{improvement.skill}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          {/* Métricas */}
          {evaluation.metrics && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8 hover:shadow-md transition-shadow duration-300">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-6 tracking-tight">Métricas de Sesión</h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              <div className="text-center p-5 bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl border border-indigo-200/40 hover:shadow-sm transition-shadow">
                <MessageSquare className="w-7 h-7 text-indigo-600 mx-auto mb-3" />
                <p className="text-3xl sm:text-4xl font-bold text-indigo-900 mb-1 tabular-nums tracking-tight">{evaluation.metrics?.totalTurns ?? 0}</p>
                <p className="text-xs sm:text-sm text-indigo-700 font-semibold">Turnos</p>
              </div>

              <div className="text-center p-5 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-200/40 hover:shadow-sm transition-shadow">
                <CheckCircle className="w-7 h-7 text-purple-600 mx-auto mb-3" />
                <p className="text-3xl sm:text-4xl font-bold text-purple-900 mb-1 tabular-nums tracking-tight">
                  {evaluation.metrics?.phasesCompleted ?? 0}/{evaluation.metrics?.totalPhases ?? 0}
                </p>
                <p className="text-xs sm:text-sm text-purple-700 font-semibold">Fases</p>
              </div>

              <div className="text-center p-5 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl border border-amber-200/40 hover:shadow-sm transition-shadow">
                <AlertTriangle className="w-7 h-7 text-amber-600 mx-auto mb-3" />
                <p className="text-3xl sm:text-4xl font-bold text-amber-900 mb-1 tabular-nums tracking-tight">{evaluation.metrics?.criticalErrors ?? 0}</p>
                <p className="text-xs sm:text-sm text-amber-700 font-semibold">Errores</p>
              </div>

              <div className="text-center p-5 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200/40 hover:shadow-sm transition-shadow">
                <Clock className="w-7 h-7 text-emerald-600 mx-auto mb-3" />
                <p className="text-2xl sm:text-3xl font-bold text-emerald-900 mb-1 tabular-nums tracking-tight">{evaluation.metrics?.duration ?? 'N/A'}</p>
                <p className="text-xs sm:text-sm text-emerald-700 font-semibold">Duración</p>
              </div>
            </div>
          </section>
          )}

          {/* Next Steps */}
          {evaluation.nextSteps?.length > 0 && (
            <section className="bg-gradient-to-br from-indigo-50/80 to-purple-50/60 border-2 border-indigo-200/60 rounded-2xl p-6 sm:p-8 shadow-sm">
              <h2 className="text-lg sm:text-xl font-bold text-indigo-900 mb-5 sm:mb-6 flex items-center gap-3 tracking-tight">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                Siguientes Pasos
              </h2>
              
              <ul className="space-y-3 sm:space-y-4">
                {evaluation.nextSteps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <span className={`
                      w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm
                      ${step.priority === 'high' ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800'}
                    `}>
                      {idx + 1}
                    </span>
                    <p className="text-sm sm:text-base text-indigo-950 pt-1 leading-relaxed font-medium">{step.action}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Footer */}
          <div className="flex justify-center pt-6 sm:pt-8">
            <button
              onClick={onClose}
              className="px-8 py-3.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationReport;
